import { StyledDatePicker, StyledPickersUnitWrapper, StyledPickersWrapper, StyledTimePicker } from "./styledComponents";
import { Typography } from "@kaltura/ds-react-components";
import dayjs from "dayjs";
import { Controller, useFormContext } from "react-hook-form";
import { FormValues } from "../../types";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { createDateTime, DEFAULT_TIME_FORMAT } from "../utils";

interface PickersUnitProps {
    dateFieldName: keyof FormValues;
    timeFieldName: keyof FormValues;
    label: string;
    dateFormat: string;
    dateDefaultValue: string;
    timeDefaultValue: string;
    timeFormat?: string;
}

const PickersUnit = (props: PickersUnitProps) => {
    const {
        dateFieldName,
        timeFieldName,
        label,
        dateFormat,
        dateDefaultValue,
        timeDefaultValue,
        timeFormat = DEFAULT_TIME_FORMAT,
    } = props;
    const { control, watch, trigger } = useFormContext<FormValues>();

    const validateTimeRange = () => {
        const startDate = watch("startTimeDate");
        const endDate = watch("endTimeDate");
        const startTime = watch("startTimeTime");
        const endTime = watch("endTimeTime");

        // Skip validation if any of the required values is missing
        if (!startDate || !endDate || !startTime || !endTime) {
            return true; // No validation error if we don't have all values yet
        }

        const startDateTime = createDateTime(startDate, startTime, dateFormat, DEFAULT_TIME_FORMAT);
        const endDateTime = createDateTime(endDate, endTime, dateFormat, DEFAULT_TIME_FORMAT);

        // Check if end datetime is before or equal to start datetime
        if (endDateTime.isBefore(startDateTime) || endDateTime.isSame(startDateTime)) {
            return translate("End time must be later than start time");
        }

        // Check if time difference is more than 24 hours
        const hoursDiff = endDateTime.diff(startDateTime, "hour");
        if (hoursDiff > 24) {
            return translate("Time gap between start and end time cannot be larger than 24 hours");
        }

        return true;
    };

    const handlePickerChange = (time: dayjs.Dayjs | null, onChange: (value: string) => void, isTime?: boolean) => {
        const formatToUse = isTime ? timeFormat : dateFormat;
        onChange(time ? time.format(formatToUse) : "");

        // Re-validate all time-related fields when any of them change
        trigger(["startTimeDate", "startTimeTime", "endTimeDate", "endTimeTime"]);
    };

    return (
        <StyledPickersUnitWrapper>
            <Typography variant={"body2Highlight"}>{label}</Typography>
            <StyledPickersWrapper>
                <Controller
                    name={dateFieldName}
                    control={control}
                    defaultValue={dateDefaultValue}
                    rules={{
                        validate: {
                            timeRange: validateTimeRange,
                        },
                    }}
                    render={({ field: { value, onChange, ref, onBlur } }) => (
                        <StyledDatePicker
                            inputRef={ref}
                            value={value ? dayjs(value, dateFormat) : null}
                            onChange={(date) => handlePickerChange(date, onChange)}
                            onBlur={onBlur}
                            format={dateFormat}
                            ariaLabel={translate("%1 date", [label])}
                            className={"chromatic-ignore"}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    )}
                />
                <Controller
                    name={timeFieldName}
                    control={control}
                    defaultValue={timeDefaultValue}
                    rules={{
                        validate: {
                            timeRange: validateTimeRange,
                        },
                    }}
                    render={({ field: { value, onChange, ref, onBlur } }) => (
                        <StyledTimePicker
                            inputRef={ref}
                            value={value ? dayjs(value, timeFormat) : null}
                            onChange={(time) => handlePickerChange(time, onChange, true)}
                            onBlur={onBlur}
                            format={timeFormat}
                            ariaLabel={translate("%1 time", [label])}
                            ampm
                            className={"chromatic-ignore"}
                            fullWidth={true}
                        />
                    )}
                />
            </StyledPickersWrapper>
        </StyledPickersUnitWrapper>
    );
};

export default PickersUnit;
